import { useEffect, useState } from "react";
import { PublicShell } from "@/components/PublicShell";
import { api, naira } from "@/lib/api";
import { Users, Wallet, TrendingUp, Store, MapPin, ReceiptText, PackageCheck, Loader2, Sprout } from "lucide-react";

const cards = [
  { key: "beneficiaries_reached", label: "Beneficiaries Reached", icon: Users, money: false },
  { key: "benefits_distributed", label: "Benefits Distributed", icon: Wallet, money: true },
  { key: "benefits_redeemed", label: "Benefits Redeemed", icon: TrendingUp, money: true },
  { key: "active_vendors", label: "Active Vendors", icon: Store, money: false },
  { key: "states_covered", label: "States Covered", icon: MapPin, money: false },
  { key: "transactions", label: "Transactions", icon: ReceiptText, money: false },
  { key: "programmes", label: "Programmes", icon: PackageCheck, money: false },
];

export default function Impact() {
  const [m, setM] = useState(null);
  useEffect(() => { api.get("/public/impact").then((r) => setM(r.data.metrics)).catch(() => setM({})); }, []);

  const isEmpty = m && (m.beneficiaries_reached || 0) === 0 && (m.active_vendors || 0) === 0 && (m.programmes || 0) === 0;

  return (
    <PublicShell>
      <section className="bg-emerald-950 tmf-grain">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-16 lg:py-20">
          <span className="text-xs font-mono uppercase tracking-widest text-emerald-300">TMF Impact Dashboard</span>
          <h1 className="mt-3 font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl text-white tracking-tight max-w-3xl">Measurable impact, in the open.</h1>
          <p className="mt-4 text-emerald-200/80 max-w-2xl">Aggregated, real-time figures across every TMF programme, sponsor and vendor nationwide.</p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-16 lg:py-24">
        {!m ? (
          <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-emerald-600" /></div>
        ) : (
          <>
            {isEmpty && (
              <div data-testid="impact-empty-state" className="mb-8 rounded-3xl border border-dashed border-emerald-200 bg-emerald-50/60 p-8 text-center">
                <div className="w-14 h-14 rounded-2xl bg-emerald-100 flex items-center justify-center mx-auto"><Sprout className="w-7 h-7 text-emerald-600" /></div>
                <h3 className="mt-4 font-display font-bold text-xl text-emerald-950">The impact story is just beginning.</h3>
                <p className="mt-2 text-sm text-gray-600 max-w-md mx-auto">As sponsors fund programmes and beneficiaries redeem their benefits, these numbers will grow in real time. Check back soon.</p>
              </div>
            )}
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {cards.map((c) => (
                <div key={c.key} data-testid={`impact-${c.key}`} className="rounded-2xl border border-emerald-100 bg-white p-6">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500 font-medium">{c.label}</span>
                    <c.icon className="w-4 h-4 text-emerald-500" />
                  </div>
                  <div className="mt-3 font-display font-bold text-3xl text-emerald-950 tabular-nums">
                    {c.money ? naira(m[c.key] || 0) : (m[c.key] || 0).toLocaleString()}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </section>
    </PublicShell>
  );
}
